//================================================================
//  Component: Triage Tab
//================================================================

//  Purpose: This contains the triage information for a digital request task

//  Properties:
//    - task = {An object, contains the digital request task details}

//  Example:
//    <TriageTab
//      task={task}
//    ></TriageTab>    

// ==============================================================

// Libraries
import React, { useState, useReducer, useContext } from 'react'

// Contexts
import { GetUser } from '../../../../Library/GlobalContexts';

// Components
import AssignTask from '../../../../Components/AssignTask/AssignTask';
import CancelRequestModal from '../Modals/CancelRequestModal';
import NewInitiative from './DigitalRequestForms/NewInitiative';
import AdditionalLicense from './DigitalRequestForms/AdditionalLicense';
import ContractRenewal from './DigitalRequestForms/ContractRenewal';
import Decommission from './DigitalRequestForms/Decommission';
import Enhancement from './DigitalRequestForms/Enhancement';

//Functions
import ConvertDate from '../../../../Library/ConvertDate';

// Images
import Expand from '../../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../../Components/Images/Icon_Expand_Teal.svg';
import Link from '../../../../Components/Images/Icon_Link_Blue.svg';

// CSS
import '../../Task.css'

export default function TriageTab({
    task
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to store the visibility of the cancel request modal
    const [cancelRequestModal, setCancelRequestModal] = useState(false);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store whether a section is expanded
    const [taskSection, setTaskSection] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'manageRequestCollapsed': false,
            'digitalRequestCollapsed': false,
        }
    );

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col gap-2 p-0'>

            {/* =========================================================== */}
            {/*  Status Label                                               */}
            {/* =========================================================== */}

            {
                [
                    {
                        'name': 'Completed',
                        'description':
                            <>
                                <b className='font-medium'>{task?.lastmodifiedby?.givenname} {task?.lastmodifiedby?.surname}</b> has completed this digital request on {ConvertDate(task?.lastmodifieddate)}.
                                <div className='mt-2 flex flex-row gap-1'>
                                    <a 
                                        href={
                                            process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? `https://devinventory.digitalservices.lendlease.com/inventory/application/${task?.applicationid}?view=overview` 
                                            : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? `https://uatinventory.digitalservices.lendlease.com/inventory/application/${task?.applicationid}?view=overview`
                                            : `https://inventory.digitalservices.lendlease.com/inventory/application/${task?.applicationid}?view=overview`
                                        } 
                                        target='_blank' 
                                        rel='noreferrer'
                                    >
                                        View application in inventory
                                    </a>
                                    <img src={Link} alt='link'></img>
                                </div>
                            </>,
                        'background': '#E7F2DA',
                        'status': 'Completed',
                        'color': '#8ABB45'
                    },
                    {
                        'name': 'Cancelled',
                        'description':
                            <>
                                <b className='font-medium'>{task?.lastmodifiedby?.givenname} {task?.lastmodifiedby?.surname}</b> has cancelled this digital request on {ConvertDate(task?.lastmodifieddate)}.
                                <div className='mt-2'>
                                    <span>
                                        Reason: {task?.cancellationreason && task?.cancellationreason !== '' ? task?.cancellationreason : 'N/A'}
                                    </span>
                                </div>
                            </>,
                        'background': '#EBEBEB',
                        'status': 'Cancelled',
                        'color': '#616161'
                    },
                    {
                        'name': 'Rejected',
                        'description':
                            <>
                                <b className='font-medium'>{task?.lastmodifiedby?.givenname} {task?.lastmodifiedby?.surname}</b> has rejected this digital request on {ConvertDate(task?.lastmodifieddate)}.
                                <div className='mt-2'>
                                    <span>
                                        Reason: {task?.rejectionreason?.length > 0 ? task?.rejectionreason : 'N/A'}
                                    </span>
                                </div>
                            </>,
                        'background': '#FCF4F6',
                        'status': 'Rejected',
                        'color': '#C4314B'
                    },
                ]
                    ?.filter((style) => style.status === task?.status)
                    .map((object, index) => (

                        <div className='px-4 py-3 mb-2' key={index} style={{ backgroundColor: object.background, borderLeft: `5px solid ${object.color}` }}>
                            <h6 className='text-black font-medium text-[18px]'>{object.name}</h6>
                            <p className='mb-0 text-gray-600'>{object.description}</p>
                        </div>

                    ))
            }

            {/* ===================================================== */}
            {/*  Manage Request                                       */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row items-center justify-between px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'manageRequestCollapsed': !taskSection.manageRequestCollapsed })}>
                    
                    {/* Manage Request */}
                    <div className='flex flex-row gap-3 items-center' onClick={() => setTaskSection({ 'manageRequestCollapsed': !taskSection.manageRequestCollapsed })}>
                        <img
                            className='cursor-pointer'
                            src={taskSection?.manageRequestCollapsed ? Collapse : Expand}
                            alt={'>'}
                        ></img>
                        <h4 className='text-[20px] m-0'> Manage Request </h4>
                    </div>

                    {/* Cancel Request */}
                    {
                        task?.status !== 'Completed' && task?.status !== 'Rejected' && task?.status !== 'Cancelled' &&
                        <div 
                            className='Secondary-Red-Button'
                            style={{ margin: '0px'}}
                            onClick={() => setCancelRequestModal(true)}
                            hidden={!getUser?.roles?.demandManagementAdmin}
                        >
                            Cancel Request
                        </div>
                    }

                </div>
                <hr className='m-0' hidden={taskSection?.manageRequestCollapsed}></hr>

                {/* ------------- MANAGE REQUEST ------------- */}

                <div className='px-[3%] pt-[2%] pb-[3%]' hidden={taskSection?.manageRequestCollapsed}>

                    <div className='FormComponent-Row'>
                        <label className='font-medium mt-0'> Assignee </label>
                        {
                            task !== undefined &&
                            <AssignTask
                                query={['roles.businessPartnerUser', '==', true]}
                                currentRequest={task}
                                styleInput={{ maxWidth: '300px', borderRadius: '5px' }}
                                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled') ? true : false}
                            ></AssignTask>
                        }
                    </div>

                    {/* =========================================================== */}
                    {/*  Date Assigned                                              */}
                    {/* =========================================================== */}

                    <div className='FormComponent-Row'>
                        <label className='font-medium'> Date Assigned </label>
                        <input
                            className='Input-Field-Text'
                            style={{ width: '300px' }}
                            type='text'
                            defaultValue={ConvertDate(task?.assigneddate)}
                            disabled
                        ></input>
                    </div>

                    {/* =========================================================== */}
                    {/*  Request Type                                               */}
                    {/* =========================================================== */}

                    <div className='FormComponent-Row'>
                        <label className='font-medium'> Type of Request </label>
                        <input
                            className='Input-Field-Text'
                            type='text'
                            defaultValue={task?.requesttype}
                            disabled
                        ></input>

                    </div>

                </div>

            </div>

            {/* ===================================================== */}
            {/*  Digital Request                                     */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'digitalRequestCollapsed': !taskSection.digitalRequestCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.digitalRequestCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'> Digital Request </h4>
                </div>
                <hr className='m-0' hidden={taskSection?.digitalRequestCollapsed}></hr>

                {/* ------------- DIGITAL REQUEST ------------- */}

                <div className='p-[3%]' hidden={taskSection?.digitalRequestCollapsed}>

                    {(() => {
                        switch (task?.requesttype) {
                            case 'New Initiative or Technology': return <NewInitiative task={task}></NewInitiative>;
                            case 'Contract Renewal': return <ContractRenewal task={task}></ContractRenewal>;
                            case 'Enhancement': return <Enhancement task={task}></Enhancement>;
                            case 'Additional Licenses or Overages': return <AdditionalLicense task={task}></AdditionalLicense>;
                            case 'Decommission': return <Decommission task={task}></Decommission>;
                            default: return 'Form not found';
                        }
                    })()}

                    {/* =========================================================== */}
                    {/*  Created By                                                 */}
                    {/* =========================================================== */}

                    <div className='FormComponent-Row'>
                        <label className='font-medium'> Created By </label>
                        <input
                            className='Input-Field-Text'
                            style={{ width: '300px'}}
                            type='text'
                            defaultValue={task?.createdby?.email}
                            disabled
                        ></input>
                    </div>

                    {/* =========================================================== */}
                    {/*  Created Date                                               */}
                    {/* =========================================================== */}

                    <div className='FormComponent-Row'>
                        <label className='font-medium'> Created Date </label>
                        <input
                            className='Input-Field-Text'
                            style={{ width: '300px' }}
                            type='text'
                            defaultValue={ConvertDate(task?.createddate)}
                            disabled
                        ></input>
                    </div>

                </div>

            </div>

            {/* =========================================================== */}
            {/*  Cancel Request Modal                                       */}
            {/* =========================================================== */}

            <CancelRequestModal
                cancelRequestModal={cancelRequestModal}
                setCancelRequestModal={setCancelRequestModal}
                task={task}
            ></CancelRequestModal>

        </div>
    );
}
